import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFogbugzCases } from "@/hooks/queries/use-fogbugz";
import { useMe } from "@/hooks/queries/use-me";
import { usePageTitle } from "@/hooks/use-page-title";
import { SearchInput } from "@/components/common/search-input";
import { PaginationControls } from "@/components/common/pagination-controls";
import { Button } from "@/components/ui/button";
import { Settings, AlertCircle, Plus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError } from "@/lib/api/client";
import { groupCasesByProjectAndArea, UNCATEGORIZED } from "@/pages/fogbugz/utils/group-cases";
import { ProjectSection } from "@/pages/fogbugz/components/project-section";
import { ProjectFilterBar } from "@/pages/fogbugz/components/project-filter-bar";
import { CaseRow } from "@/pages/fogbugz/components/case-row";
import { ViewModeToggle, type ViewMode } from "@/pages/fogbugz/components/view-mode-toggle";

const PAGE_SIZE_STORAGE_KEY = "fogbugz.ticketList.pageSize";
const VIEW_MODE_STORAGE_KEY = "fogbugz.ticketList.viewMode";
const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

function getStoredPageSize(): number {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = Number(stored);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch {
    // localStorage unavailable
  }
  return 25;
}

function getStoredViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === "grouped" || stored === "flat") return stored;
  } catch {
    // localStorage unavailable
  }
  return "grouped";
}

export function TicketListPage() {
  usePageTitle("FogBugz Tickets");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);

  // Persist pageSize to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    } catch {
      // localStorage unavailable
    }
  }, [pageSize]);

  // Persist viewMode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // localStorage unavailable
    }
  }, [viewMode]);

  // Reset page synchronously when search or pageSize changes to avoid double-fetch
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const { data: meData, isLoading: meLoading } = useMe();
  const hasFogbugzKey = meData?.hasFogbugzKey ?? false;

  // Skip the cases query while we're still loading the user OR when we
  // already know there's no key configured — both produce known failures
  // and waste a round-trip per render.
  const {
    data: casesData,
    isLoading: casesLoading,
    error: casesError,
  } = useFogbugzCases({ query: searchQuery, page, pageSize }, {
    enabled: !meLoading && hasFogbugzKey,
  });

  const cases = casesData?.data ?? [];
  const hasMore = casesData?.meta?.hasMore ?? false;

  const filteredCases = useMemo(
    () => selectedProject ? cases.filter((c) => (c.sProject?.trim() || UNCATEGORIZED) === selectedProject) : cases,
    [cases, selectedProject]
  );

  const groupedCases = useMemo(() => groupCasesByProjectAndArea(filteredCases), [filteredCases]);

  // Auto-reset to page 1 if we get an empty page beyond the first
  useEffect(() => {
    if (!casesLoading && cases.length === 0 && page > 1) {
      setPage(1);
    }
  }, [casesLoading, cases.length, page]);

  // If the selected project filter is no longer present on the current page
  // (e.g., user paginated past it), clear the filter so the user isn't stuck
  // on an empty "No tickets for the selected project" view with no chip to
  // un-press. The filter bar is derived from the current page only, so the
  // selected chip would otherwise vanish from the UI.
  useEffect(() => {
    if (casesLoading || !selectedProject || cases.length === 0) return;
    const projectsOnPage = new Set(
      cases.map((c) => c.sProject?.trim() || UNCATEGORIZED)
    );
    if (!projectsOnPage.has(selectedProject)) {
      setSelectedProject(null);
    }
  }, [casesLoading, cases, selectedProject]);

  // 503 = server-side ConfigurationError (FogBugz integration not configured
  // on the server). Distinguish this from the user-side "no key saved" path
  // (which surfaces as `!hasFogbugzKey`, not as an HTTP error).
  const is503Error = casesError instanceof ApiError && casesError.response.status === 503;

  if (meLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (is503Error) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              FogBugz Integration Not Configured
            </CardTitle>
            <CardDescription>
              The FogBugz integration is not configured on the server. Please contact your
              administrator to enable it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!hasFogbugzKey) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              FogBugz API Key Required
            </CardTitle>
            <CardDescription>
              You haven&apos;t saved a FogBugz API key yet. Add one in Settings to start viewing
              tickets.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              Go to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FogBugz Tickets</h1>
          <p className="text-muted-foreground mt-1">View and manage your FogBugz cases</p>
        </div>
        <Button onClick={() => navigate("/fogbugz/formulator")}>
          <Plus className="mr-2 h-4 w-4" />
          New Ticket
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search cases..."
          className="max-w-md"
        />
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
      </div>

      {!casesLoading && cases.length > 0 && (
        <ProjectFilterBar
          cases={cases}
          selectedProject={selectedProject}
          onSelectProject={setSelectedProject}
        />
      )}

      {casesLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading tickets...</div>
        </div>
      ) : casesError && !is503Error ? (
        <Card className="border-red-500/50 bg-red-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="font-medium">Failed to load tickets</p>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {casesError instanceof Error ? casesError.message : "Unknown error"}
            </p>
          </CardContent>
        </Card>
      ) : cases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {searchQuery ? "No tickets match your search." : "No tickets found."}
            </p>
          </CardContent>
        </Card>
      ) : filteredCases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No tickets for the selected project.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {viewMode === "grouped" ? (
            [...groupedCases.entries()]
              .map(([projectName, areaMap]) => ({
                projectName,
                areaMap,
                totalCount: [...areaMap.values()].reduce(
                  (sum, areaCases) => sum + areaCases.length,
                  0
                ),
              }))
              .filter(({ totalCount }) => totalCount > 0)
              .map(({ projectName, areaMap, totalCount }) => (
                <ProjectSection
                  key={projectName}
                  projectName={projectName}
                  areaMap={areaMap}
                  totalCount={totalCount}
                  onNavigate={(id) => navigate(`/fogbugz/tickets/${id}`)}
                />
              ))
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-medium text-sm text-muted-foreground">Case #</th>
                      <th className="px-4 py-3 font-medium text-sm text-muted-foreground">Title</th>
                      <th className="px-4 py-3 font-medium text-sm text-muted-foreground">Status</th>
                      <th className="px-4 py-3 font-medium text-sm text-muted-foreground">Priority</th>
                      <th className="px-4 py-3 font-medium text-sm text-muted-foreground">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCases.map((caseItem) => (
                      <CaseRow
                        key={caseItem.ixBug}
                        caseItem={caseItem}
                        onNavigate={(id) => navigate(`/fogbugz/tickets/${id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t">
                <PaginationControls
                  showing={filteredCases.length}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  currentPage={page}
                  hasPreviousPage={page > 1}
                  hasNextPage={hasMore && page < 1000}
                  onPreviousPage={() => setPage((p) => Math.max(1, p - 1))}
                  onNextPage={() => setPage((p) => Math.min(1000, p + 1))}
                  isLoading={casesLoading}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
              </div>
            </Card>
          )}
          {viewMode === "grouped" && (
            <Card>
              <div className="px-4 py-3">
                <PaginationControls
                  showing={filteredCases.length}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  currentPage={page}
                  hasPreviousPage={page > 1}
                  hasNextPage={hasMore && page < 1000}
                  onPreviousPage={() => setPage((p) => Math.max(1, p - 1))}
                  onNextPage={() => setPage((p) => Math.min(1000, p + 1))}
                  isLoading={casesLoading}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
